import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    translateX: number;
    translateY: number;
    translateZ: number;
    rotateX: number;
    rotateY: number;
    rotateZ: number;
    scaleX: number;
    scaleY: number;
    skewX: number;
    skewY: number;
    perspective: number;
    copied: boolean;
}

export default class CssTransformGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        translateX: 0, translateY: 0, translateZ: 0,
        rotateX: 0, rotateY: 0, rotateZ: 0,
        scaleX: 1, scaleY: 1,
        skewX: 0, skewY: 0,
        perspective: 600,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get transformValue(): string {
        const { translateX, translateY, translateZ, rotateX, rotateY, rotateZ, scaleX, scaleY, skewX, skewY } = this.state;
        const parts: string[] = [];
        if (translateX || translateY || translateZ) parts.push(`translate3d(${translateX}px, ${translateY}px, ${translateZ}px)`);
        if (rotateX) parts.push(`rotateX(${rotateX}deg)`);
        if (rotateY) parts.push(`rotateY(${rotateY}deg)`);
        if (rotateZ) parts.push(`rotateZ(${rotateZ}deg)`);
        if (scaleX !== 1 || scaleY !== 1) parts.push(`scale(${scaleX}, ${scaleY})`);
        if (skewX) parts.push(`skewX(${skewX}deg)`);
        if (skewY) parts.push(`skewY(${skewY}deg)`);
        return parts.join(' ') || 'none';
    }

    private get cssCode(): string {
        return `transform: ${this.transformValue};\ntransform-style: preserve-3d;\nperspective: ${this.state.perspective}px;`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    reset = () => {
        this.setState({
            translateX: 0, translateY: 0, translateZ: 0,
            rotateX: 0, rotateY: 0, rotateZ: 0,
            scaleX: 1, scaleY: 1, skewX: 0, skewY: 0,
        });
    };

    render() {
        const { translateX, translateY, translateZ, rotateX, rotateY, rotateZ, scaleX, scaleY, skewX, skewY, perspective, copied } = this.state;

        const slider = (label: string, value: number, min: number, max: number, step: number, fmt: (v: number) => string, onChange: (v: number) => void) => (
            <div className={genStyles.field}>
                <label>{label}: <strong>{fmt(value)}</strong></label>
                <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(+e.target.value)} />
            </div>
        );

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}>
                        <span className={genStyles.sectionTitle}>Translate</span>
                        <button className={genStyles.iconBtn} onClick={this.reset}>↺ Reset</button>
                    </div>
                    {slider('x', translateX, -200, 200, 1, v => `${v}px`, v => this.setState({ translateX: v }))}
                    {slider('y', translateY, -200, 200, 1, v => `${v}px`, v => this.setState({ translateY: v }))}
                    {slider('z', translateZ, -200, 200, 1, v => `${v}px`, v => this.setState({ translateZ: v }))}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Rotate</span></div>
                    {slider('x', rotateX, -180, 180, 1, v => `${v}°`, v => this.setState({ rotateX: v }))}
                    {slider('y', rotateY, -180, 180, 1, v => `${v}°`, v => this.setState({ rotateY: v }))}
                    {slider('z', rotateZ, -180, 180, 1, v => `${v}°`, v => this.setState({ rotateZ: v }))}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Scale &amp; skew</span></div>
                    {slider('scale x', scaleX, 0.1, 3, 0.05, v => v.toFixed(2), v => this.setState({ scaleX: v }))}
                    {slider('scale y', scaleY, 0.1, 3, 0.05, v => v.toFixed(2), v => this.setState({ scaleY: v }))}
                    {slider('skew x', skewX, -60, 60, 1, v => `${v}°`, v => this.setState({ skewX: v }))}
                    {slider('skew y', skewY, -60, 60, 1, v => `${v}°`, v => this.setState({ skewY: v }))}
                    {slider('perspective', perspective, 100, 2000, 1, v => `${v}px`, v => this.setState({ perspective: v }))}
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                preview={
                    <div style={{ perspective: `${perspective}px`, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <div style={{ transform: this.transformValue, width: 100, height: 100, background: '#6366f1', borderRadius: 8, transition: 'transform 0.2s ease' }} />
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
